import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Assets, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { map, ReplaySubject, takeUntil, filter, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import UserUtil, { getUserInitials } from 'app/core/utilities/user-util';
import { AppState } from 'app/types';
import { getResizeUri } from 'app/utils/image';
import { Loggable } from 'app/utils/logging/loggable';

import { notUndefined } from '../../../utils/stream-util';
import { COLORS, COLOR_WHITE, FONT_FAMILY } from '../constants';
import { FallbackVideoCanvas } from '../social-pixi-canvas/FallbackVideoCanvas';
import { Player } from '../social-pixi-canvas/player';
import { ExtendedPresenter, socialAppSelectors } from '../state/app';

import { BrowserService } from './browser.service';
import { DeviceService } from './device/device.service';
import { SceneService } from './scene.service';
import { VideoRoomService } from './video-room.service';

const UNDEFINED_STREAM = { id: uuidv4(), stream: undefined };
@Injectable()
export class ToasterVideoService extends Loggable {
  private videoElement?: HTMLVideoElement = undefined;
  private toastStreamSubscriptions = new Map<string, Subscription>();

  private readonly logContext = { class: 'Toaster' };
  private readonly sizeOfToaster = 340;
  private insetByX = 50;
  private insetByY = 40;
  private lineColor = 0xffffff;

  private toasterPlayer?: Player;
  private profileImageURI?: string;

  private wrapperContainer?: Container;
  private circleBorder?: Graphics;
  private circleMask?: Graphics;
  private initials?: Text;
  private initialsCircleSmooth?: Sprite;
  private avatarSprite?: Sprite;
  private avatarBorder?: Graphics;
  private videoContainer?: Container;
  private isLocal?: boolean;

  // Fallback canvas
  private useFallbackCanvas = false;
  private fallbackVideoCanvas?: FallbackVideoCanvas;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private sceneService: SceneService,
    private deviceService: DeviceService,
    private videoRoomService: VideoRoomService,
    private store: Store<AppState>,
    private browserService: BrowserService,
  ) {
    super();
    this.setDisplayName('ToasterVideoService');

    // Video Element
    const video = document.createElement('video');
    video.width = 220;
    video.height = 220;
    this.videoElement = video;

    this.store
      .select(socialAppSelectors.getToasterDetails)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((presenter) => this.handlePresenterChange(presenter));

    this.store
      .select(socialAppSelectors.getConvoDetails)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((presenter) => this.handlePresenterChange(presenter));

    // Subscribe to resize needed
    this.sceneService.resizeNeeded$.subscribe((resized) => {
      if (resized) {
        this.resize();
      }
    });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_fallback_canvas_video)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((useFallbackCanvas) => (this.useFallbackCanvas = useFallbackCanvas));
  }

  public init() {
    this.destroyed$ = new ReplaySubject(1);
    // Initialize Wrapper Sprite
    this.wrapperContainer = new Container();
    this.wrapperContainer.name = 'toaster-wrapper';
    this.wrapperContainer.eventMode = 'static';

    // TODO: refactor to use nativeCursor directive
    this.wrapperContainer
      .on('pointerover', () => {
        this.sceneService.sceneManager?.hideCustomCursor();
      })
      .on('pointerout', () => {
        this.sceneService.sceneManager?.reRenderCustomCursor();
        this.sceneService.sceneManager?.showCustomCursor();
      });

    // Position Wrapper Sprite
    this.positionWrapperSprite();
  }

  public async addToaster(toasterId: string): Promise<void> {
    this.info('addToaster', { toasterId });
    const toasterPlayer = this.sceneService.getPlayer(toasterId);

    if (!toasterPlayer) {
      this.info('toasterPlayer does not exist');
      return;
    }
    this.wrapperContainer?.removeChildren();

    this.toasterPlayer = toasterPlayer;
    this.profileImageURI = toasterPlayer?.user.profileImageURI;

    // Handle circle masks
    this.generateCircleMasks();

    // Handle Video Sprite
    await this.generateVideo();

    if (this.wrapperContainer) {
      this.wrapperContainer.visible = true;
    }

    // Generate initials or avatar
    if (!this.profileImageURI) {
      this.generateInitials();
    } else {
      await this.generateAvatar();
    }

    // Add Children and Wrapper Sprite to the pixi world
    const isWrapperConatiner = !!this.sceneService?.sceneManager?.app?.stage.getChildByName('toaster-wrapper');
    if (this.wrapperContainer && !isWrapperConatiner) {
      this.sceneService?.sceneManager?.app?.stage.addChild(this.wrapperContainer);
    }
  }

  // Remove sprites and children
  public removeToaster() {
    if (this.toasterPlayer) {
      this.info('removeToaster', { player: this.toasterPlayer?.user });
    }

    // Clear video subscriptions
    this.clearStreamSubscriptions();

    this.videoElement?.pause();
    this.videoElement!.srcObject = null;
    this.videoElement!.removeAttribute('src');
    // Resets the element
    this.videoElement!.load();
    this.videoElement!.oncanplay = null;
    this.videoElement!.removeEventListener('loadeddata', this.playListener);

    this.fallbackVideoCanvas?.destroy();
    this.fallbackVideoCanvas = undefined;

    // Destroy elements
    if (this.wrapperContainer) {
      this.wrapperContainer.visible = false;
      this.wrapperContainer.removeChildren();
    }

    // Set toaster player and initials or avatar to undefined
    // Resets elements for next toaster or ice breaker
    this.toasterPlayer = undefined;
    this.initials = undefined;
    this.initialsCircleSmooth = undefined;
    this.avatarBorder = undefined;
    this.avatarSprite = undefined;
  }

  private setVideoEnabledTo(enabled: boolean) {
    if (this.videoContainer) {
      const visible = enabled;
      this.videoContainer.visible = visible;
    }
  }

  private handlePresenterChange = (presenter?: ExtendedPresenter) => {
    this.clearStreamSubscriptions();

    let subscription: Subscription;
    if (presenter) {
      const { isLocal, userId, videoMuted } = presenter;

      if (!this.toasterPlayer && videoMuted) {
        return;
      }
      this.setVideoEnabledTo(!videoMuted);
      this.isLocal = isLocal;
      // Local presenters pull their media stream directly from the camera via device service.
      if (isLocal) {
        subscription = this.createLocalVideo();
      } else {
        subscription = this.createParticipantVideo(userId);
      }

      // Currently only support one toaster
      // Keeping the subscription will allow us to have more than one toaster: TO DO
      this.toastStreamSubscriptions.set(userId, subscription);
    }
  };

  private createLocalVideo(): Subscription {
    return this.deviceService.cameraStream$
      .pipe(
        map((stream) => (stream ? { stream, id: stream.id } : UNDEFINED_STREAM)),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange({ stream, id }));
  }

  private createParticipantVideo(userId: string): Subscription {
    return this.videoRoomService.participantVideoStream$
      .pipe(
        map((streams) => streams[userId] ?? UNDEFINED_STREAM),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange({ stream, id }));
  }

  private handleMediaStreamChange({ id, stream }: { stream?: MediaStream; id: string }) {
    if (!this.videoElement) {
      this.warn('handleMediaStreamChange: Failed to get video element');
      return;
    }

    // Avoid resetting the video source if this media stream has already been set. This avoids video flicker when a
    // different presenter joins or mutes their video
    // TODO: This is not working as expected. Need to investigate further
    // if (this.videoElement.getAttribute('data-stream-id') === id) {
    //   this.trace('handleMediaStreamChange: skipping video stream update, stream already set as source');
    //   return;
    // }

    this.videoElement.srcObject = stream ?? null;
    this.videoElement.setAttribute('data-stream-id', id);

    if (!this.videoElement.onloadeddata) {
      this.videoElement.addEventListener('loadeddata', this.playListener);
    }
  }

  private playListener = async () => {
    try {
      await this.videoElement!.play();
    } catch (e) {
      this.error('Failed to play video', e);
    }
  };

  private clearStreamSubscriptions() {
    this.toastStreamSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.toastStreamSubscriptions.clear();
  }

  private createVideoTexture({
    element,
    height,
    width,
  }: {
    element: HTMLVideoElement;
    width: number;
    height: number;
  }): Texture {
    // if (this.useFallbackCanvas) {
    //   this.warn('Using fallback canvas video renderer for toaster');
    //   this.fallbackVideoCanvas = new FallbackVideoCanvas(element, width, height);
    //   return this.fallbackVideoCanvas.texture;
    // }

    return Texture.from(element);
  }

  private async generateVideo() {
    const container = new Container();
    container.name = 'toaster-video-container';
    const videoSprite = new Sprite();
    videoSprite.width = this.sizeOfToaster;
    videoSprite.height = this.sizeOfToaster;
    videoSprite.anchor.set(0.5, 0.5);
    container.addChild(videoSprite);
    window.dispatchEvent(new Event('resize'));

    // Handle Toaster Video Element
    if (this.videoElement) {
      let height = this.sizeOfToaster;
      let width = this.sizeOfToaster;
      try {
        const res = (this.videoElement.srcObject as MediaStream).getVideoTracks()[0].getSettings();
        height = res?.height ?? height;
        width = res?.width ?? width;
        if (this.browserService.isFirefox) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const media: any = this.videoElement.srcObject;
          height = media?.canvas.height ?? height;
          width = media?.canvas.width ?? width;
        }
      } catch (e) {
        this.warn('Could not determine actual video resolution for toast', this.videoElement, e);
      }

      await this.isVideoReady();
      const pixiTexture =
        this.videoElement.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA
          ? this.createVideoTexture({ element: this.videoElement, width, height })
          : Texture.EMPTY;

      videoSprite.texture = pixiTexture;
      if (videoSprite) {
        // Circle Mask -------------------------------------------------------------
        const circleMaskRadius = this.sizeOfToaster / 2;
        // the sprite gets scaled based on the image provided so we need this adjusted by same
        const circleMask = new Graphics().beginFill(COLORS.maskColor).drawCircle(0, 0, circleMaskRadius).lineStyle(0);

        container.mask = circleMask;
        container.addChild(circleMask);

        // Set the scale again after the video starts playing. Some browsers (firefox) don't supply settings for the video
        // if the video is supplied by a webrtc media track.
        const applyRatio = () => {
          if (!this.videoContainer || !this.videoElement) {
            this.error('applyRatio: missing videoContainer or videoElement');
            return;
          }
          const newHeightRatio = this.sizeOfToaster / this.videoElement.videoHeight;
          const newWidthRatio = this.sizeOfToaster / this.videoElement.videoWidth;
          const newRatio = Math.max(newHeightRatio, newWidthRatio);
          if (this.isLocal) {
            videoSprite.scale.set(newRatio * -1, newRatio);
          } else {
            videoSprite.scale.set(newRatio, newRatio);
          }
        };

        this.videoElement.onresize = applyRatio;
        this.videoElement.onplay = applyRatio;
        this.videoElement.onload = applyRatio;

        this.videoContainer = container;
        applyRatio();
      }
    }
  }

  private async isVideoReady(): Promise<boolean> {
    if (this.videoElement && this.videoElement.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
      return true;
    } else {
      await delayFor(500);
      return this.isVideoReady();
    }
  }

  private generateInitials() {
    if (
      !this.wrapperContainer ||
      !this.videoContainer ||
      !this.toasterPlayer ||
      !this.circleMask ||
      !this.circleBorder
    ) {
      this.error('Error: Could not generate initials');
      return;
    }
    const user = this.toasterPlayer?.user ?? {};
    const bgColor = UserUtil.getProfileColorInt(user);
    const userInitials = getUserInitials(user);
    this.initials = new Text(userInitials, {
      fontFamily: FONT_FAMILY,
      fontSize: 100,
      fill: COLOR_WHITE,
      align: 'center',
    });
    this.initials.anchor.set(0.5, 0.5);
    const initialsCircle = new Graphics();
    initialsCircle.name = 'toaster-initials-circle';
    initialsCircle
      .beginFill(bgColor)
      .lineStyle(6, this.lineColor)
      .drawCircle(0, 0, this.sizeOfToaster / 2 - 3)
      .endFill();
    const initialsTexture = this.sceneService.sceneManager?.app?.renderer.generateTexture(initialsCircle);
    this.initialsCircleSmooth = new Sprite(initialsTexture);
    this.initialsCircleSmooth.name = 'toaster-initials-circle-smooth';
    this.initialsCircleSmooth.anchor.set(0.5, 0.5);

    // Initials
    this.wrapperContainer.addChild(
      this.initialsCircleSmooth,
      this.initials,
      this.videoContainer,
      this.circleMask,
      this.circleBorder,
    );
  }

  private async generateAvatar(): Promise<void> {
    // Avatar Sprite
    const profileImageURI = getResizeUri({ uri: this.profileImageURI ?? '', width: this.sizeOfToaster });
    await Assets.load(profileImageURI);
    const texture = Texture.from(profileImageURI);
    const avatarSprite = new Sprite(texture);
    avatarSprite.height = this.sizeOfToaster;
    avatarSprite.width = this.sizeOfToaster;
    avatarSprite.anchor.set(0.5, 0.5);

    // Avatar Mask
    // the sprite gets scaled based on the image provided so we need this adjusted by same
    const avatarMask = new Graphics();
    avatarMask
      .beginFill(COLORS.maskColor)
      .drawCircle(0, 0, this.sizeOfToaster / 2 / avatarSprite.scale.x)
      .endFill()
      .lineStyle(0);
    avatarSprite.mask = avatarMask;
    // TODO: v8 Only Containers can have children, i think we can lose this line
    // avatarSprite.addChild(avatarMask);

    // Avatar Border
    const avatarBorder = new Graphics()
      .drawCircle(0, 0, this.sizeOfToaster / 2 - 3)
      .lineStyle({ width: 6, color: this.lineColor });
    avatarBorder.name = 'toaster-avatar-border';
    this.avatarSprite = avatarSprite;
    this.avatarBorder = avatarBorder;

    if (this.avatarBorder && this.avatarSprite && this.videoContainer && this.circleMask && this.circleBorder) {
      this.wrapperContainer?.addChild(
        this.avatarSprite,
        this.avatarBorder,
        this.videoContainer,
        this.circleMask,
        this.circleBorder,
      );
    }
  }

  // This Circle Masks will always be on top of all layers (video/avatar/initials)
  private generateCircleMasks() {
    // Build Circle Mask
    if (this.wrapperContainer) {
      // the sprite gets scaled based on the image provided so we need this adjusted by same
      this.circleMask = new Graphics()
        .beginFill(COLORS.maskColor)
        .drawCircle(0, 0, this.sizeOfToaster / 2)
        .lineStyle(0);
      this.circleMask.name = 'toaster-circle-mask';
      this.wrapperContainer.mask = this.circleMask;
    }

    // Build circle Border
    this.circleBorder = new Graphics()
      .drawCircle(0, 0, this.sizeOfToaster / 2 - 2)
      .lineStyle({ width: 4, color: this.lineColor });
    this.circleBorder.name = 'toaster-circle-border';
  }

  private resize() {
    this.positionWrapperSprite();
  }

  // Position Sprite
  private positionWrapperSprite() {
    if (!this.wrapperContainer) {
      return;
    }
    const x = this.sceneService.screenWidth - this.insetByX - this.sizeOfToaster / 2;
    const y = this.insetByY + this.sizeOfToaster / 2;
    this.wrapperContainer.position.set(x, y);
  }

  public destroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.videoElement!.setAttribute('data-stream-id', '');

    // Destroy all children
    if (this.wrapperContainer) {
      this.wrapperContainer.destroy({
        children: true,
        texture: false,
      });
    }
    // Only remove video and wrapper border/mask when service is destroyed
    this.videoContainer = undefined;
    this.circleBorder = undefined;
    this.circleMask = undefined;
    this.wrapperContainer = undefined;
    this.removeToaster();
  }
}
